import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Row } from 'react-table';
import XLSX from 'xlsx';
import { round } from 'lodash';
import { ButtonV2 as Button } from '@praktio/common-ui';

import { RootState } from '../../../../ducks/store';
import { Timezone } from '../../../../utils/constants';
import { formateDateTime } from '../LoadDateTime/LoadDateTime';
import { generateReportXlsxFileName } from '../../../../utils/functions';
import { CourseName, Learner } from '../TableSummaryReport/TableSummaryReport.types';
import { LearnerFilter } from '../GeneralReport.enum';
import { DownloadReportModal, DownloadOption } from '../../../common/modals/DownloadReportModal';

type Props = {
  learnerData: Optional<Learner[]>;
  courseData: Optional<CourseName[]>;
  hasSingleSelectedCourse?: boolean;
  hasFilters?: boolean;
  filteredRows?: Row<Learner>[];
  learnerFilter?: LearnerFilter;
  searchQuery?: string;
};

const DownloadSummaryReportButton = ({
  learnerData,
  courseData,
  hasSingleSelectedCourse,
  hasFilters = false,
  filteredRows,
  learnerFilter,
  searchQuery,
}: Props) => {
  const missingData = !learnerData || !courseData;

  const [downloadDisabled, setDownloadDisabled] = useState(missingData);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const { user, loadDate, activeDeselectedCourses } = useSelector((state: RootState) => ({
    user: state.auth.authenticatedUser,
    loadDate: new Date(state.report.loadDate),
    activeDeselectedCourses: state.teamsSlice.activeDeselectedCourses,
  }));

  /**
   * Builds a human-readable description of the applied filters.
   * @returns A string describing all active filters, e.g., "Status = Haven't Started, Search = \"john\""
   */
  const getAppliedFilterDescription = (): string => {
    const descriptions: string[] = [];

    if (learnerFilter && learnerFilter !== LearnerFilter.All) {
      const filterLabels: Record<LearnerFilter, string> = {
        [LearnerFilter.All]: '',
        [LearnerFilter.HaventStarted]: "Haven't Started",
        [LearnerFilter.HaventCompleted]: "Haven't Completed",
        [LearnerFilter.Completed]: 'Completed',
      };

      descriptions.push(`Status = ${filterLabels[learnerFilter]}`);
    }

    if (searchQuery) {
      descriptions.push(`Search = "${searchQuery}"`);
    }

    return descriptions.join(', ');
  };

  const executeDownload = (option: DownloadOption) => {
    if (downloadDisabled || !learnerData || !courseData) return;

    setDownloadDisabled(true);
    setShowDownloadModal(false);

    setTimeout(() => {
      setDownloadDisabled(false);
    }, 2000);

    const isFiltered = option === DownloadOption.Filtered;
    const dataSource =
      isFiltered && filteredRows ? filteredRows.map((row) => row.original) : learnerData;
    const reportName = isFiltered ? 'Praktio Summary Report (Filtered)' : 'Praktio Summary Report';

    const filteredCourseData = courseData.filter(
      (course) => !activeDeselectedCourses.includes(course._id),
    );

    const loadTime = formateDateTime(loadDate);

    const workbook = XLSX.utils.book_new();

    const courseNames = filteredCourseData?.map((course: CourseName) => course.title) || [];
    const headers = ['Name', 'Email', ...courseNames];

    // Add 'Total % Complete' header if there are multiple courses.
    // Should be the third column.
    if (!hasSingleSelectedCourse) {
      headers.splice(2, 0, 'Total % Complete');
    }

    const filterDescription = isFiltered ? getAppliedFilterDescription() : '';
    const filterNote = filterDescription ? ` [FILTERED: ${filterDescription}]` : '';
    const topRow = [
      `This report was downloaded on ${loadTime}, by ${user?.firstName} ${user?.lastName}. All dates and times are in their local time zone (${Timezone}).${filterNote}`,
    ];

    // Create learner data rows
    const data = dataSource?.map((learner) => {
      const courseProgress = learner.courseProgress || {};

      const progress =
        filteredCourseData?.map((course: CourseName) => {
          return courseProgress[course._id] || courseProgress[course._id] === 0
            ? courseProgress[course._id]
            : 'N/A';
        }) || [];

      const row = [learner.name, learner.email, ...progress];

      // Add 'Total % Complete' if there are multiple courses
      // Should be the third column
      if (!hasSingleSelectedCourse) {
        row.splice(2, 0, learner.totalPercentageComplete);
      }

      return row;
    });

    // Create average row
    const averageRow = [
      'Average',
      '',
      ...(filteredCourseData?.map(({ _id }) => {
        const total = dataSource?.reduce((acc, learner) => {
          const courseProgress = learner.courseProgress[_id];
          return courseProgress || courseProgress === 0 ? acc + courseProgress : acc;
        }, 0);

        const rowsLength = dataSource?.filter((learner) => {
          const courseProgress = learner.courseProgress[_id];
          return courseProgress || courseProgress === 0;
        }).length;

        if (!total || !rowsLength) return 0;

        return round(total / rowsLength, 2);
      }) || []),
    ];

    // Add 'Total % Complete' if there are multiple courses
    // Should be the third column
    if (!hasSingleSelectedCourse) {
      averageRow.splice(
        2,
        0,
        round(
          dataSource?.reduce((acc, learner) => acc + (learner?.totalPercentageComplete || 0), 0) /
            dataSource?.length,
          2,
        ),
      );
    }

    const worksheet = XLSX.utils.aoa_to_sheet([topRow, headers, ...data, averageRow]);

    const range = XLSX.utils.decode_range(worksheet['!ref'] as string);

    // Convert values to percentages (excluding the first two columns: Name and Email)
    for (let row = 2; row < range.e.r + 1; row++) {
      for (let cell = 2; cell < range.e.c + 1; cell++) {
        const tableCell = worksheet[XLSX.utils.encode_cell({ r: row, c: cell })];

        if (!tableCell || tableCell.v === 'N/A') continue;

        tableCell.v = tableCell.v / 100;
        tableCell.t = 'n';
        tableCell.z = '0.00%';
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary Report');
    XLSX.writeFile(workbook, generateReportXlsxFileName(reportName, loadDate));
  };

  const handleDownloadClick = () => {
    if (hasFilters) {
      setShowDownloadModal(true);
      return;
    }

    executeDownload(DownloadOption.Full);
  };

  return (
    <>
      <Button
        disabled={downloadDisabled}
        variant="primary"
        icon={{ name: 'download', position: 'left' }}
        onClick={handleDownloadClick}
      >
        {missingData ? 'Oh no! Something went wrong' : 'Download Report'}
      </Button>

      <DownloadReportModal
        show={showDownloadModal}
        onHide={() => setShowDownloadModal(false)}
        onDownload={executeDownload}
        isDownloading={downloadDisabled}
      />
    </>
  );
};

export default DownloadSummaryReportButton;
